import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Button, Circle, Popover, Separator, Text, YStack } from 'tamagui';
import type { AuthUser } from '@mrrmlab/auth';
import { useAppAuth } from '../app-context';
import { useSignOut } from '../hooks/use-sign-out';

function initialsOf(user: AuthUser | null): string {
  const source = user?.name ?? user?.preferredUsername ?? user?.email ?? '';
  if (!source) return '?';
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

export function ProfileMenu() {
  const auth = useAppAuth();
  const { signOut, pending } = useSignOut();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!auth) return;
    auth.getUser().then(setUser);
  }, [auth]);

  const label = initialsOf(user);
  const close = () => setOpen(false);

  return (
    <Popover placement="bottom-end" allowFlip open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Circle
          size={36}
          backgroundColor="$color5"
          hoverStyle={{ backgroundColor: '$color6' }}
          pressStyle={{ backgroundColor: '$color7' }}
          cursor="pointer"
          aria-label="Profil-Menü"
        >
          <Text fontSize="$3" fontWeight="700">{label}</Text>
        </Circle>
      </Popover.Trigger>

      <Popover.Content
        borderWidth={1}
        borderColor="$borderColor"
        backgroundColor="$background"
        padding="$2"
        gap="$1"
        minWidth={200}
        elevate
      >
        <Popover.Arrow borderWidth={1} borderColor="$borderColor" />
        <YStack gap="$1">
          <Link to="/einstellungen/mail" onClick={close} style={{ textDecoration: 'none', color: 'inherit' }}>
            <MenuItem label="Einstellungen" />
          </Link>
          <Link to="/konto" onClick={close} style={{ textDecoration: 'none', color: 'inherit' }}>
            <MenuItem label="Konto" />
          </Link>
          <Separator />
          <Button
            size="$3"
            chromeless
            justifyContent="flex-start"
            disabled={pending}
            onPress={() => { close(); void signOut(); }}
            theme="red"
            aria-label="Abmelden"
          >
            Abmelden
          </Button>
        </YStack>
      </Popover.Content>
    </Popover>
  );
}

function MenuItem({ label }: { label: string }) {
  return (
    <YStack
      paddingVertical="$2"
      paddingHorizontal="$3"
      borderRadius="$3"
      hoverStyle={{ backgroundColor: '$color3' }}
    >
      <Text fontSize="$4">{label}</Text>
    </YStack>
  );
}
